import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormatIndianNumber } from "../utiles/FormatIndianNumber";
import TableLayout from "./TableLayout";
import useLocale from "../locales";

const RdTable = ({ showTable, timeUnit, historyData,
  shouldGenerateReport,
  setShouldGenerateReport, calcDone}) => {
  const { textData } = useLocale();
  
  const [reportData, setReportData] = useState([]);
  const { investmentAmmount, timePeriod, intrestRate, totalValue } = useSelector(
    (state) => state.rdCalculator
  );

  useEffect(() => {
    if (historyData){
      generateReportFromHistory();
    }
    
  }, [showTable, investmentAmmount, timePeriod, intrestRate, totalValue, timeUnit,historyData]);

  useEffect(() => {
    if (shouldGenerateReport) {
      generateReportData();
      setShouldGenerateReport(false);
    }else if (!calcDone) {
    setReportData([]); 
  }
  generateReportFromHistory();
}, [shouldGenerateReport, calcDone, historyData]);


  const generateReportFromHistory = () => {
    if(!Array.isArray(historyData) || historyData.length === 0){
      return;
    }
    
    const rdData = historyData[0];
    const data = [];
    const monthlyDeposit = Number(historyData[0]?.invstmentAmount);
    const annualRate = Number(historyData[0]?.interestRate);
    
    let periods = Number(historyData[0]?.timePeriod);
    const N = 4;

    
    
    const tenureFromHistory = Number(historyData[0]?.tenure);
    const originalTimeUnit = historyData[0]?.timeUnit || 'Months';
    
    if (originalTimeUnit === 'Years') {
      periods = tenureFromHistory * 12; 
    } else {
      periods = Number(historyData[0]?.timePeriod); 
    }
    
 
    const monthlyRate = (annualRate) / 100;
    
    const monthlyFutureValues = [];
    for (let i = 0; i < periods; i++) {
      const monthsLeft = periods - i;
      const time = monthsLeft / 12;
      const compoundFactor = Math.pow(1 + monthlyRate / N, N * time);
      const futureValue = monthlyDeposit * compoundFactor;
      monthlyFutureValues.push(futureValue);
    }
  
    // Display data based on original time unit selection
    if (originalTimeUnit === 'Years') {
      const totalYears = Math.ceil(periods / 12);
      for (let year = 1; year <= totalYears; year++) {
        const tillMonth = Math.min(year * 12, periods);
        const totalInvested = monthlyDeposit * tillMonth;
        const maturityValue = monthlyFutureValues.slice(0, tillMonth).reduce((sum, val) => sum + val, 0);
        const interest = maturityValue - totalInvested;
  
        data.push({
          year: `${year}`,
          monthlydeposite: monthlyDeposit,
          totalamount: totalInvested,
          totalintrestearnd: interest,
          cumulativeamount: maturityValue,
        });
      }
    } else {
      // Show monthly breakdown when original selection was 'Months'
      for (let month = 1; month <= periods; month++) {
        const totalInvested = monthlyDeposit * month;
        const maturityValue = monthlyFutureValues.slice(0, month).reduce((sum, val) => sum + val, 0);
        const interest = maturityValue - totalInvested;
  
        data.push({
          year: `${month}`,
          monthlydeposite: monthlyDeposit,
          totalamount: totalInvested,
          totalintrestearnd: interest,
          cumulativeamount: maturityValue,
        });
      }
    }
  
    // Round only once, for final display
    const roundedData = data.map((row) => ({
      ...row,
      totalamount: Math.round(row.totalamount),
      totalintrestearnd: Math.round(row.totalintrestearnd),
      cumulativeamount: Math.round(row.cumulativeamount),
    }));
    setReportData(roundedData);
  };

  const generateReportData = () => {
    const data = [];
    const monthlyDeposit = Number(investmentAmmount);
    const annualRate = Number(intrestRate);
    let periods = Number(timePeriod);
    const N = 4;
    periods = timeUnit === 'Months' ? Number(timePeriod) : Number(timePeriod) * 12;
  
    const monthlyRate = (annualRate) / 100;
    const monthlyFutureValues = [];
  
    // Precompute future value of each deposit
    for (let i = 0; i < periods; i++) {
      const monthsLeft = periods - i;
      const time = monthsLeft / 12;
      const compoundFactor = Math.pow(1 + monthlyRate / N, N * time);
      const futureValue = monthlyDeposit * compoundFactor;
      // const futureValue = monthlyDeposit * Math.pow(1 + monthlyRate, monthsLeft);
      monthlyFutureValues.push(futureValue);
    }
  
    if (timeUnit === "Years") {
      const totalYears = Math.ceil(periods / 12);
      for (let year = 1; year <= totalYears; year++) {
        const tillMonth = Math.min(year * 12, periods);
        const totalInvested = monthlyDeposit * tillMonth;
        const maturityValue = monthlyFutureValues.slice(0, tillMonth).reduce((sum, val) => sum + val, 0);
        const interest = maturityValue - totalInvested;
  
        data.push({
          year: `${year}`,
          monthlydeposite: monthlyDeposit,
          totalamount: totalInvested,
          totalintrestearnd: interest,
          cumulativeamount: maturityValue,
        });
      }
    } else {
      for (let month = 1; month <= periods; month++) {
        const totalInvested = monthlyDeposit * month;
        const maturityValue = monthlyFutureValues.slice(0, month).reduce((sum, val) => sum + val, 0);
        const interest = maturityValue - totalInvested;
  
        data.push({
          year: `${month}`,
          monthlydeposite: monthlyDeposit,
          totalamount: totalInvested,
          totalintrestearnd: interest,
          cumulativeamount: maturityValue,
        });
      }
    }

    // Round only once, for final display
    const roundedData = data.map((row) => ({
      ...row,
      totalamount: Math.round(row.totalamount),
      totalintrestearnd: Math.round(row.totalintrestearnd),
      cumulativeamount: Math.round(row.cumulativeamount),
    }));
    setReportData(roundedData);
  };

  const getDisplayTimeUnit = () => {
    if (historyData && historyData.length > 0) {
      return historyData[0]?.timeUnit || 'Months';
    }
    return timeUnit;
  };
  
  const displayTimeUnit = getDisplayTimeUnit();
  
  const columns = [
    { 
      key: 'year', 
      label: displayTimeUnit === "Months" ? textData.tableLabels.month : textData.tableLabels.year, 
      format: (value) => value 
    },
    { 
      key: 'monthlydeposite', 
      label: `${textData.tableLabels.monthlydeposite} (${textData.currency})`, 
      format: (value) => `${FormatIndianNumber(Math.round(value))}` 
    },
    { 
      key: 'totalamount', 
      label: `${textData.tableLabels.totalamount} (${textData.currency})`, 
      format: (value) => `${FormatIndianNumber(Math.round(value))}` 
    },
    { 
      key: 'totalintrestearnd', 
      label: `${textData.tableLabels.totalintrestearnd} (${textData.currency})`, 
      format: (value) => `${FormatIndianNumber(Math.round(value))}` 
    },
    { 
      key: 'cumulativeamount', 
      label: `${textData.tableLabels.cumulativeamount} (${textData.currency})`, 
      format: (value) => `${FormatIndianNumber(Math.round(value))}` 
    }
  ];

  return (
    <TableLayout
      showTable={showTable}
      title={`${textData.filterDropDown.RDCalc} ${textData.report}`}
      data={reportData}
      columns={columns}
      analyticsEventName="download_rd_report"
      reportTitle={textData.reportTitle.rdReport}
      fileName="rd_detail"   
    />
  );
};

export default RdTable;